import type { FC } from 'react';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiSmileyBold, PiSmileyMehBold, PiSmileySadBold } from 'react-icons/pi';
import { Stack, useTheme } from '@mui/material';

import { noDataAvailableStyles } from 'src/theme/sharedStyles';
import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import type { WidgetFooterLegendLabel } from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetFooterLegend from 'src/components/followUp/common/WidgetFooterLegend/WidgetFooterLegend';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

import { transformNpsData } from './helper';
import { useExportOptions } from './hooks';
import NpsQuestion from './NpsQuestion';

import { styles } from 'src/components/dashboard/DashboardPage/widgets/DeepDiveQuestionsWidget/index.styles';

export const LEGEND_DETRACTOR_TEST_ID = 'legend-detractor-test-id';
export const LEGEND_PASSIVE_TEST_ID = 'legend-passive-test-id';
export const LEGEND_PROMOTER_TEST_ID = 'legend-promoter-test-id';

const DeepDiveQuestionsWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('common');
  const ariaId = useId();
  const { colors } = useTheme();

  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.DEEP_DIVE_QUESTIONS,
    config.id,
  );

  const { data, isFetching, errorMsg, refetch, comparisonData, queryFilters } =
    useWidgetQuery<StackedNumbersList>(endpoint);

  const npsData = useMemo(() => transformNpsData(data), [data]);
  const labels: WidgetFooterLegendLabel[] = useMemo(() => {
    const hasDetractors = npsData?.some((q) => q.detractor > 0) ?? false;
    const hasPassive = npsData?.some((q) => q.passive > 0) ?? false;
    const hasPromoters = npsData?.some((q) => q.promoter > 0) ?? false;
    const hasNotAnswered = npsData?.some((q) => q.notAnswered > 0) ?? false;

    const allLabels = [];

    if (hasDetractors) {
      allLabels.push({
        color: colors.error[600],
        comparisonColor: comparisonData && colors.comparison[400],
        name: (
          <Stack
            data-test-id={LEGEND_DETRACTOR_TEST_ID}
            sx={{ justifyContent: 'center' }}
          >
            <PiSmileySadBold size={24} color={colors.error[600]} />
          </Stack>
        ),
      });
    }

    if (hasPassive) {
      allLabels.push({
        color: colors.neutral[500],
        comparisonColor: comparisonData && colors.comparison[200],
        name: (
          <Stack
            data-test-id={LEGEND_PASSIVE_TEST_ID}
            sx={{
              justifyContent: 'center',
            }}
          >
            <PiSmileyMehBold size={24} color={colors.neutral[500]} />
          </Stack>
        ),
      });
    }

    if (hasPromoters) {
      allLabels.push({
        color: colors.tertiary[600],
        comparisonColor: comparisonData && colors.comparison[100],
        name: (
          <Stack
            data-test-id={LEGEND_PROMOTER_TEST_ID}
            sx={{ justifyContent: 'center' }}
          >
            <PiSmileyBold size={24} color={colors.tertiary[700]} />
          </Stack>
        ),
      });
    }

    if (hasNotAnswered) {
      allLabels.push({
        color: colors.yellow[500],
        comparisonColor: comparisonData && colors.comparison[300],
        name: (
          <Stack sx={{ justifyContent: 'center' }}>{t('common:n/a')}</Stack>
        ),
      });
    }

    if (!hasDetractors && !hasPassive && !hasPromoters && !hasNotAnswered) {
      allLabels.push({
        color: 'transparent',
        name: t('common:noDataAvailable'),
        customBackgroundSx: noDataAvailableStyles.noDataAvailable,
      });
    }

    return allLabels;
  }, [colors, comparisonData, t, npsData]);

  const comparisonNpsData = useMemo(
    () => transformNpsData(comparisonData),
    [comparisonData],
  );

  const exportOptions = useExportOptions({
    data: npsData,
    comparisonData: comparisonNpsData,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      refetch={refetch}
      loading={isFetching}
      error={errorMsg}
      minHeight="auto"
      exportOptions={exportOptions}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        data?.totalObjects === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : (
          <WidgetFooterLegend labels={labels} />
        )
      }
    >
      <Stack sx={styles.npsQuestionWrapper}>
        {npsData?.map((npsQuestion) => (
          <NpsQuestion
            key={npsQuestion.question}
            question={npsQuestion.question}
            nps={{ ...npsQuestion }}
            comparisonNps={comparisonNpsData?.find(
              (comparisonQuestion) =>
                comparisonQuestion.question === npsQuestion.question,
            )}
          />
        ))}
      </Stack>
    </WidgetFrame>
  );
};

export default DeepDiveQuestionsWidget;
